import { useEffect, useState } from "react";
import httpService from "../../services/httpService";
import Button from "react-bootstrap/Button";
import Image from "react-bootstrap/Image";
import { Accordion, Collapse } from "react-bootstrap";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import LoadingFade from "../LoadingFade";
import Form from "react-bootstrap/Form";
import OverlayTrigger from "react-bootstrap/esm/OverlayTrigger";
import Tooltip from "react-bootstrap/esm/Tooltip";
import {FaHandSparkles, FaRobot, FaSpinner, FaRegTrashAlt, FaCog, FaInfoCircle} from "react-icons/fa";
import Badge from "react-bootstrap/Badge";

const EpisodeDiscussionModalComponent = ({ episode, podcastClubGuid }) => {
    const [epi, setEpi] = useState({});
    const [discussions, setDiscussions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [generating, setGenerating] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [activeKey, setActiveKey] = useState(null);
    const [showAddForm, setShowAddForm] = useState(false);
    const [newDiscussion, setNewDiscussion] = useState({ title: '', body: '' });
    const [saving, setSaving] = useState(false);
    const [editStates, setEditStates] = useState({});
    const [deleteOpen, setDeleteOpen] = useState({});
    const [deleting, setDeleting] = useState({});
    const [similarity, setSimilarity] = useState(5);
    const [showSimilarityInfo, setShowSimilarityInfo] = useState(false);

    const pivotGuid = episode?.pivot?.guid;

    const loadDiscussions = () => {
        httpService.get(`/dash/club/${podcastClubGuid}/episode/${pivotGuid}/discussions`)
            .then((res) => setDiscussions(res.data.data ?? []))
            .catch((err) => setErrorMessage(err.response?.data?.message || 'Error loading discussions'))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        setLoading(true);
        httpService.get(`/dash/wizard/get-podcast-club-episode/${episode.pivot.podcast_club_id}/${episode.pivot.podcast_episode_id}`)
            .then((res) => setEpi(res.data.episode ?? {}))
            .catch(() => {});

        loadDiscussions();
    }, [episode]);

    const generateDiscussions = async () => {
        setGenerating(true);
        setErrorMessage('');
        try {
            const res = await httpService.post(`/dash/club/${podcastClubGuid}/episode/${pivotGuid}/discussions/generate`, { similarity });
            setDiscussions(res.data.data ?? []);
        } catch (err) {
            setErrorMessage(err.response?.data?.message || 'Error generating discussions');
        } finally {
            setGenerating(false);
        }
    };

    const handleCreate = async () => {
        if (!newDiscussion.title || !newDiscussion.body) return;
        setSaving(true);
        try {
            await httpService.post(`/dash/club/${podcastClubGuid}/discussions`, {
                title: newDiscussion.title,
                body: newDiscussion.body,
                episode_pivot_id: episode.pivot.id,
                status: 'hidden',
            });
            setNewDiscussion({ title: '', body: '' });
            setShowAddForm(false);
            loadDiscussions();
        } catch (err) {
            setErrorMessage(err.response?.data?.message || 'Error creating discussion');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (guid) => {
        setDeleting(prev => ({ ...prev, [guid]: true }));
        try {
            await httpService.delete(`/dash/club/${podcastClubGuid}/discussions/${guid}`);
            setDeleteOpen(prev => ({ ...prev, [guid]: false }));
        } catch (err) {
            setErrorMessage(err.response?.data?.message || 'Error deleting discussion');
            setDeleting(prev => ({ ...prev, [guid]: false }));
        }
    };

    const startEdit = (disc) => {
        setDeleteOpen(prev => ({ ...prev, [disc.guid]: false }));
        setEditStates(prev => ({ ...prev, [disc.guid]: { title: disc.title, body: disc.body, saving: false } }));
    };

    const cancelEdit = (guid) => {
        setEditStates(prev => {
            const next = { ...prev };
            delete next[guid];
            return next;
        });
    };

    const handleEditChange = (guid, field, value) => {
        setEditStates(prev => ({ ...prev, [guid]: { ...prev[guid], [field]: value } }));
    };

    const handleSave = async (disc) => {
        const edit = editStates[disc.guid];
        setEditStates(prev => ({ ...prev, [disc.guid]: { ...prev[disc.guid], saving: true } }));
        try {
            await httpService.put(`/dash/club/${podcastClubGuid}/discussions/${disc.guid}`, {
                title: edit.title,
                content: edit.body,
                episode_pivot_id: episode.pivot.id,
            });
            setDiscussions(prev => prev.map(d =>
                d.guid === disc.guid ? { ...d, title: edit.title, body: edit.body } : d
            ));
            cancelEdit(disc.guid);
        } catch (err) {
            setErrorMessage(err.response?.data?.message || 'Error saving discussion');
            setEditStates(prev => ({ ...prev, [disc.guid]: { ...prev[disc.guid], saving: false } }));
        }
    };

    return (
        <div>
            <LoadingFade loading={loading} stateKey={loading ? 'loading' : 'not-loading'}>
                <Row className="mb-3">
                    <Col sm={10} className="d-flex flex-column justify-content-center">
                        <h5>{epi.title}</h5>
                    </Col>
                    <Col sm={2} className="text-center">
                        {epi.featured_image && (
                            <Image
                                src={epi.featured_image}
                                alt={epi.title}
                                width={48}
                                height={48}
                                style={{ objectFit: 'cover', borderRadius: 8 }}
                                loading="lazy"
                            />
                        )}
                    </Col>
                </Row>

                {/* Generate button — prominent at top */}
                <Row className="mb-3 justify-content-center">
                    <Col sm={12} className="d-flex align-items-center gap-2 justify-content-end">
                        <div className="d-flex align-items-center gap-2" style={{ opacity: discussions.length <= 4 ? 1 : 0, pointerEvents: discussions.length <= 4 ? 'auto' : 'none', transition: 'opacity 0.3s ease' }}>

                                <Button
                                    variant={showSimilarityInfo ? 'secondary' : 'outline-secondary'}
                                    size="md"
                                    onClick={() => setShowSimilarityInfo(v => !v)}
                                >
                                    <FaInfoCircle />
                                </Button>

                                <OverlayTrigger
                                    placement="top"
                                    overlay={
                                        <Tooltip>
                                            Similarity to existing: {similarity} — {similarity <= 3 ? 'very different' : similarity <= 6 ? 'somewhat similar' : similarity <= 8 ? 'fairly similar' : 'almost identical'}
                                        </Tooltip>
                                    }
                                >
                                    <div className="d-flex align-items-center gap-1" style={{ width: 120 }}>
                                        <small className="text-muted" style={{ whiteSpace: 'nowrap' }}>1</small>
                                        <Form.Range
                                            min={1}
                                            max={10}
                                            step={1}
                                            value={similarity}
                                            onChange={e => setSimilarity(Number(e.target.value))}
                                            disabled={generating}
                                            style={{ margin: 0 }}
                                        />
                                        <small className="text-muted" style={{ whiteSpace: 'nowrap' }}>10</small>
                                    </div>
                                </OverlayTrigger>

                                &nbsp;
                                &nbsp;

                                <OverlayTrigger placement="top" overlay={<Tooltip>Generate discussion prompts using AI</Tooltip>}>
                                    <Button
                                        variant="outline-secondary"
                                        size="md"
                                        disabled={generating}
                                        onClick={generateDiscussions}
                                    >
                                        {generating ? <FaSpinner className="loading_spinner" /> : <FaRobot />}
                                    </Button>
                                </OverlayTrigger>
                        </div>

                        <OverlayTrigger placement="top" overlay={<Tooltip>Add discussion manually</Tooltip>}>
                            <Button
                                variant={showAddForm ? 'success' : 'outline-success'}
                                size="md"
                                onClick={() => setShowAddForm(v => !v)}
                            >
                                <FaHandSparkles />
                            </Button>
                        </OverlayTrigger>
                    </Col>
                </Row>

                <Collapse in={showSimilarityInfo}>
                    <div className="m-0">
                        <div className="pb-3">
                            <div className="border rounded p-2 bg-light">
                                <small className="text-muted">
                                    <strong>Similarity slider (1–10)</strong> controls how different the AI-generated discussions should be from existing ones.<br />
                                    <strong>1</strong> — completely different topics and angles &nbsp;·&nbsp;
                                    <strong>5</strong> — related themes from a different angle &nbsp;·&nbsp;
                                    <strong>10</strong> — almost identical, only wording differs
                                </small>
                            </div>
                        </div>
                    </div>
                </Collapse>

                {errorMessage && (
                    <Row className="mb-2">
                        <Col>
                            <div className="alert alert-danger py-2">{errorMessage}</div>
                        </Col>
                    </Row>
                )}

                {/* Manual create form */}
                <Collapse in={showAddForm}>
                    <div className="mb-3">
                        <Row>
                            <Col sm={12}>
                                <Form.Group className="mb-2">
                                    <Form.Label>Title</Form.Label>
                                    <Form.Control
                                        type="text"
                                        placeholder="Discussion title"
                                        value={newDiscussion.title}
                                        onChange={e => setNewDiscussion(p => ({ ...p, title: e.target.value }))}
                                        disabled={saving}
                                    />
                                </Form.Group>
                                <Form.Group className="mb-2">
                                    <Form.Label>Discussion prompt / body</Form.Label>
                                    <Form.Control
                                        as="textarea"
                                        rows={3}
                                        placeholder="What question or topic should listeners discuss?"
                                        value={newDiscussion.body}
                                        onChange={e => setNewDiscussion(p => ({ ...p, body: e.target.value }))}
                                        disabled={saving}
                                    />
                                </Form.Group>
                                <div className="d-flex gap-2 justify-content-end">
                                    <Button size="sm" variant="light" onClick={() => setShowAddForm(false)} disabled={saving}>Cancel</Button>
                                    <Button
                                        size="sm"
                                        variant="success"
                                        onClick={handleCreate}
                                        disabled={saving || !newDiscussion.title || !newDiscussion.body}
                                    >
                                        {saving ? <FaSpinner className="loading_spinner" /> : 'Create'}
                                    </Button>
                                </div>
                            </Col>
                        </Row>
                    </div>
                </Collapse>

                {/* Discussions list */}
                {discussions.length > 0 && (
                    <Row className="mb-1">
                        <Col>
                            <Accordion activeKey={activeKey} onSelect={k => setActiveKey(k)}>
                                {discussions.map((disc, index) => {
                                    const edit = editStates[disc.guid];
                                    const isDeleting = !!deleting[disc.guid];
                                    return (
                                        <Accordion.Item key={disc.guid} eventKey={String(index)}>
                                            <Accordion.Header>
                                                <span className="me-2">{disc.title}</span>
                                                {disc.status === 'published' && (
                                                    <Badge bg="success" style={{ fontSize: '0.65rem' }}>published</Badge>
                                                )}
                                                {isDeleting && <FaSpinner className="loading_spinner ms-2 text-danger" />}
                                                {edit?.saving && <FaSpinner className="loading_spinner ms-2 text-secondary" />}
                                            </Accordion.Header>
                                            <Accordion.Body onExited={() => { if (deleting[disc.guid]) { setDiscussions(prev => prev.filter(d => d.guid !== disc.guid)); loadDiscussions(); } }}>
                                                <p className="mb-2">{disc.body}</p>
                                                <div className="d-flex gap-2 justify-content-end my-2">
                                                    <OverlayTrigger placement="top" overlay={<Tooltip>Edit discussion</Tooltip>}>
                                                        <Button size="md" disabled={isDeleting} variant={edit ? 'secondary' : 'outline-secondary'} onClick={() => edit ? cancelEdit(disc.guid) : startEdit(disc)}>
                                                            <FaCog />
                                                        </Button>
                                                    </OverlayTrigger>
                                                    <OverlayTrigger placement="top" overlay={<Tooltip>Delete discussion</Tooltip>}>
                                                        <Button size="md" disabled={isDeleting} variant={'outline-danger'} onClick={() => { cancelEdit(disc.guid); setDeleteOpen(prev => ({ ...prev, [disc.guid]: !prev[disc.guid] })); }}>
                                                            <FaRegTrashAlt />
                                                        </Button>
                                                    </OverlayTrigger>
                                                </div>
                                                <Collapse className={'EDMC collapse-wrapper'} in={!!deleteOpen[disc.guid]} onExited={() => { if (deleting[disc.guid]) { setActiveKey(null); } }}>
                                                    <div className="m-0 p-0 EDMC delete-collapse">
                                                        <div className="py-1">
                                                            <div className="border border-danger rounded p-2 bg-light d-flex align-items-center justify-content-between gap-2">
                                                                <small className="text-danger">Delete <strong>{disc.title}</strong>?</small>
                                                                <div className="d-flex gap-2">
                                                                    <Button size="md" variant="light" disabled={isDeleting} onClick={() => setDeleteOpen(prev => ({ ...prev, [disc.guid]: false }))}>Cancel</Button>
                                                                    <Button size="md" variant="outline-danger" disabled={isDeleting} onClick={() => handleDelete(disc.guid)}>
                                                                        {isDeleting ? <FaSpinner className="loading_spinner" /> : 'Delete'}
                                                                    </Button>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </Collapse>
                                                <Collapse in={!!edit}>
                                                    <div>
                                                        <Form.Group className="mb-3">
                                                            <Form.Label>Title</Form.Label>
                                                            <Form.Control
                                                                type="text"
                                                                value={edit?.title ?? ''}
                                                                onChange={e => handleEditChange(disc.guid, 'title', e.target.value)}
                                                                disabled={edit?.saving || isDeleting}
                                                            />
                                                        </Form.Group>
                                                        <Form.Group className="mb-3">
                                                            <Form.Label>Body</Form.Label>
                                                            <Form.Control
                                                                as="textarea"
                                                                rows={3}
                                                                value={edit?.body ?? ''}
                                                                onChange={e => handleEditChange(disc.guid, 'body', e.target.value)}
                                                                disabled={edit?.saving || isDeleting}
                                                            />
                                                        </Form.Group>
                                                        <div className="d-flex gap-2 justify-content-end">
                                                            <Button size="md" variant="light" onClick={() => cancelEdit(disc.guid)} disabled={edit?.saving || isDeleting}>Cancel</Button>
                                                            <Button size="md" variant="outline-secondary" onClick={() => handleSave(disc)} disabled={edit?.saving || isDeleting}>
                                                                {edit?.saving ? <FaSpinner className="loading_spinner" /> : <><FaCog />&nbsp;Save</>}
                                                            </Button>
                                                        </div>
                                                    </div>
                                                </Collapse>
                                            </Accordion.Body>
                                        </Accordion.Item>
                                    );
                                })}
                            </Accordion>
                        </Col>
                    </Row>
                )}

                {!loading && discussions.length === 0 && (
                    <Row>
                        <Col className="text-center text-muted py-4">
                            <p>No discussions yet. Generate some with AI or add one manually.</p>
                        </Col>
                    </Row>
                )}
            </LoadingFade>
        </div>
    );
};

export default EpisodeDiscussionModalComponent;
